import type { ErrorEvent, EventHint } from '@sentry/nextjs';
import { DIAGNOSTIC_READ_ERROR_TYPE, releasedDiagnosticReadText } from './diagnosticRead';
import { safeErrorType, sanitizeLogArgs } from './logger';

// ============================================================================
// Sentry-Gemeinschaftskonfiguration (P2 Error-Tracking).
// ----------------------------------------------------------------------------
// DORMANT ohne DSN: Sentry.init wird in den instrumentation-Dateien nur mit
// gesetztem DSN aufgerufen → ohne Account/Env passiert nichts (kein Build-/Runtime-
// Effekt). Bewusst KEIN withSentryConfig/Build-Plugin (Turbopack-sicher); Source-Map-
// Upload ist ein optionaler Folge-Schritt. Nur Fehler, kein Performance-Tracing
// (Free-Tier schonen).
//
// ----------------------------------------------------------------------------
// PII-Härtung (Sicherheitsanalyse 2026-07-26, H2/M2/N9)
// ----------------------------------------------------------------------------
// `sendDefaultPii: false` reicht NICHT — das war die zentrale Fehlannahme der
// bisherigen Konfiguration. Im installierten SDK liefert
// `defaultPiiToCollectionOptions(false)` für Cookies und Header ein FILTER-Objekt
// (`{ deny: PII_HEADER_SNIPPETS }`), nicht `false`. Die requestData-Integration
// wertet danach `include.cookies = dataCollection.cookies !== false` aus — ein
// Objekt ist ungleich `false`, also blieb die Erhebung AN. Der Typ sagt es
// ausdrücklich: `cookies?: CollectBehavior` mit `@default true`. Die Filterung
// über `SENSITIVE_COOKIE_NAME_SNIPPETS` greift ausserdem nur im Span-Pfad, den
// `tracesSampleRate: 0` gar nicht erzeugt.
//
// Folge wäre gewesen: bei jedem Server-Fehler ging der rohe `Cookie`-Header mit —
// inklusive Supabase-Auth-Cookie (Access UND Refresh-Token) der betroffenen
// Lehrperson bzw. des Kindes. Ein Refresh-Token überlebt einen Passwortwechsel.
//
// Deshalb zwei voneinander unabhängige Ebenen:
//  1. `dataCollection` schaltet die Erhebung explizit ab, statt sie aus
//     `sendDefaultPii` abzuleiten.
//  2. `scrubEvent` räumt fail-closed nach — wirkt auch, wenn ein SDK-Update die
//     Defaults erneut ändert, und deckt `request.url` ab, das laut SDK-Kommentar
//     («URL is always included») gar nicht abschaltbar ist.
// ============================================================================

const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;

/**
 * Produktions-React wirft «Minified React error #418; visit …?args[]=…». Die
 * Nummer allein (418 Hydrations-Konflikt, 423 wiederherstellbar, 425 Textinhalt)
 * ist reine Technik und macht Hydrations-Fehler diagnostizierbar; Argumente
 * und URL (dort steckt der DOM-Diff) bleiben draussen (Befund 09.09.2026:
 * vier Eltern-Ereignisse ohne verwertbare Nummer).
 */
const REACT_ERROR_CODE_RE = /Minified React error #(\d{1,4})\b/;

/**
 * Maskiert Zugangs-Token, die in URLs stecken. Betrifft drei Public-Token-Flows:
 *  - `/eltern/<token>`   — Eltern-Zugang (Token allein reicht nicht, PIN bleibt)
 *  - `/api/cal/<token>`  — ICS-Feed, Token allein GENÜGT für den Zugriff
 *  - `?token=<…>`        — Display/Beamer, Token allein GENÜGT (Kinder-Vornamen)
 *
 * Zusätzlich wird das URL-Fragment komplett entfernt: die Karten-Login-URL
 * `/k#<token>` trägt das 128-Bit-Kartentoken im Fragment. Das erreicht zwar nie
 * den Server (docs/06 §2), wohl aber `location.href` im Browser-SDK. Fragmente
 * haben für die Fehlerdiagnose keinen Wert — der Pfad bleibt erhalten.
 */
export function redactUrlSecrets(url: string): string {
  return url
    .replace(/#.*$/, '')
    .replace(/(\/eltern\/)[^/?#]+/gi, '$1[token]')
    .replace(/(\/api\/cal\/)[^/?#]+/gi, '$1[token]')
    .replace(/([?&]token=)[^&#]*/gi, '$1[token]');
}

/**
 * Entfernt E-Mail-Adressen, Session-Cookies und Token, bevor das Event rausgeht.
 * Bewusst fail-closed: gelöscht wird alles, was für die Diagnose entbehrlich
 * ist, aber Zugangsdaten tragen kann.
 *
 * Exception-Texte entfallen bis auf feste technische Kürzel. Einzige
 * Freigabe eines ganzen Textes: der Lesefehler einer Diagnosequelle
 * (`DiagnosticReadError`), dessen Text aus Quelle der Freigabeliste,
 * HTTP-Status und Fehlercode neu gebaut wird (Störung 22.10.2026: Sentry
 * zeigte nur «Error», die Ursache stand allein im Vercel-Log).
 */
export function scrubEvent(event: ErrorEvent, hint?: EventHint): ErrorEvent {
  const diagnosticReadText = releasedDiagnosticReadText(hint?.originalException);
  const logMessage = typeof event.extra?.logMessage === 'string'
    ? event.extra.logMessage.replace(EMAIL_RE, '[email]')
    : undefined;
  const args = sanitizeLogArgs(Array.isArray(event.extra?.args) ? event.extra.args : []);
  if (event.extra) {
    event.extra = { ...(logMessage ? { logMessage } : {}), ...(args.length ? { args } : {}) };
  }
  // NodeSystemError kopiert eigene Error-Felder; ExtraErrorData verwendet den
  // Exception-Namen als Context-Key. Diese SDK-Pfade können cause/Antworten tragen.
  if (event.contexts) {
    delete event.contexts.node_system_error;
    for (const ex of event.exception?.values ?? []) {
      if (ex.type) delete event.contexts[ex.type];
    }
  }

  if (event.exception?.values?.length) {
    event.exception.values = event.exception.values.map((ex) => {
      // Die Client-Instrumentierung erkennt diese beiden DOM-Fehlerklassen
      // am Text und ergänzt ihr Head-Inventar. Nur feste Ersatztexte behalten.
      const originalValue = ex.value;
      const domOperation = typeof originalValue === 'string'
        ? ['removeChild', 'insertBefore'].find((operation) => originalValue.includes(operation))
        : undefined;
      const type = safeErrorType(ex.type);
      const reactCode = typeof originalValue === 'string' ? REACT_ERROR_CODE_RE.exec(originalValue)?.[1] : undefined;
      const value = diagnosticReadText && type === DIAGNOSTIC_READ_ERROR_TYPE
        ? diagnosticReadText
        : domOperation
          ? `DOM ${domOperation} failed`
          : reactCode
            ? `React error #${reactCode}`
            : logMessage ?? type;
      const stacktrace = ex.stacktrace ? {
        ...ex.stacktrace,
        frames: ex.stacktrace.frames?.map((frame) => {
          const safeFrame = { ...frame };
          delete safeFrame.vars;
          delete safeFrame.module_metadata;
          return safeFrame;
        }),
      } : undefined;
      const mechanism = ex.mechanism ? { ...ex.mechanism } : undefined;
      if (mechanism) delete mechanism.data;
      return { type, value, ...(stacktrace ? { stacktrace } : {}), ...(mechanism ? { mechanism } : {}) };
    });
    // Das SDK kann denselben Exception-Text zusätzlich als message führen.
    if (event.message) event.message = logMessage ?? event.exception.values[0]?.value;
  } else if (event.message) {
    event.message = event.message.replace(EMAIL_RE, '[email]');
  }
  if (event.logentry) {
    event.logentry = {
      message: event.exception?.values?.length
        ? logMessage ?? event.exception.values[0]?.value
        : event.logentry.message?.replace(EMAIL_RE, '[email]'),
    };
  }

  if (event.request) {
    // Cookies und Header tragen die Supabase-Session — nie diagnostisch nötig.
    delete event.request.cookies;
    delete event.request.headers;
    // Request-Bodies von Server Actions enthalten Kind-Freitexte und Klarnamen.
    delete event.request.data;

    if (typeof event.request.url === 'string') {
      event.request.url = redactUrlSecrets(event.request.url);
    }
    if (typeof event.request.query_string === 'string') {
      event.request.query_string = event.request.query_string.replace(
        /(^|&)(token=)[^&]*/gi,
        '$1$2[token]',
      );
    }
  }

  // Das SDK formatiert alle Console-Argumente auch in crumb.message. Nur data
  // zu löschen liess Rohtexte deshalb weiterhin durch (F03, 2026-09-06).
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.filter((crumb) => crumb.category !== 'console');
  }
  for (const crumb of event.breadcrumbs ?? []) {
    if (typeof crumb.message === 'string') {
      crumb.message = crumb.message.replace(EMAIL_RE, '[email]');
    }
    const url = crumb.data?.url;
    if (typeof url === 'string') {
      crumb.data = { ...crumb.data, url: redactUrlSecrets(url) };
    }
  }

  return event;
}

/** Gemeinsame Optionen für Server-, Edge- und Client-Init. */
export const sentryCommonOptions = {
  tracesSampleRate: 0, // nur Fehler, kein Performance-Tracing
  sendDefaultPii: false, // keine IPs/User-Infos automatisch anhängen
  // Explizit statt abgeleitet — siehe Kopfkommentar. `false` ist hier das
  // einzige, was die requestData-Integration wirklich abschaltet.
  dataCollection: {
    cookies: false,
    httpHeaders: { request: false, response: false },
    httpBodies: [],
    queryParams: false,
  },
  // Das SDK normalisiert `extra` vor beforeSend; mit dem Standard 3 wurden die
  // Listenfelder in `extra.args` (Watchdog-`breaches`, `notes`) zu «[Array]» und
  // danach vom Scrubber verworfen (Befund 22.10.2026, CHECKPOINT-EDU-R).
  normalizeDepth: 4,
  beforeSend: (event: ErrorEvent, hint: EventHint) => scrubEvent(event, hint),
};
